/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
import {useCallback, useEffect, useRef, useState} from 'react';
import {Dialog, DialogContent, DialogContentText} from '@mui/material';

type NetworkInformation = {
  readonly effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  addEventListener(
    type: 'change',
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: 'change',
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
} & EventTarget;

const getConnection = (): NetworkInformation | undefined =>
  (navigator as any).connection ||
  (navigator as any).mozConnection ||
  (navigator as any).webkitConnection;

const NetworkStatusWithQuality: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [effectiveType, setEffectiveType] = useState<
    NetworkInformation['effectiveType'] | 'unknown'
  >(() => getConnection()?.effectiveType || 'unknown');

  const connectionRef = useRef<NetworkInformation | undefined>(getConnection());

  const handleConnectionChange = useCallback(() => {
    setEffectiveType(connectionRef.current?.effectiveType || 'unknown');
  }, []);

  useEffect(() => {
    const handleOffline = () => setIsOnline(false);
    const handleOnline = () => setIsOnline(true);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    if (connectionRef.current) {
      setEffectiveType(connectionRef.current.effectiveType || 'unknown');
      connectionRef.current.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      if (connectionRef.current) {
        connectionRef.current.removeEventListener(
          'change',
          handleConnectionChange,
        );
      }
    };
  }, [handleConnectionChange]);

  useEffect(() => {
    if (!isOnline) {
      setShowDrawer(true);
      setErrorMessage('Check connection');
    } else if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      setShowDrawer(true);
      setErrorMessage('Poor connection, may lag');
    } else {
      setShowDrawer(false);
      setErrorMessage('');
    }
  }, [isOnline, effectiveType]);

  return (
    <Dialog
      open={showDrawer}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px !important',
        },
      }}
      aria-labelledby="alert-connection-network-dialog-title"
      aria-describedby="alert-connection-network-dialog-description">
      <DialogContent sx={{padding: '15px'}}>
        <DialogContentText
          textAlign={'center'}
          fontFamily={'Geogrotesque Wide'}
          fontSize={14}
          fontWeight={'500'}
          id="alert-connection-network-dialog-description">
          {errorMessage}
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default NetworkStatusWithQuality;
